const MAGIC = 0x444a504b; // "DJPK"
const HEADER_BYTES = 16;

export interface PeakData {
  /** Interleaved min/max pairs, one pair per bucket. */
  readonly values: Int8Array;
  readonly count: number;
  readonly bucketsPerSecond: number;
}

export function parsePeaks(buffer: ArrayBuffer): PeakData {
  if (buffer.byteLength < HEADER_BYTES) {
    throw new Error("peak data is truncated");
  }
  const view = new DataView(buffer);
  if (view.getUint32(0, false) !== MAGIC) {
    throw new Error("not a peak file");
  }

  const version = view.getUint16(4, true);
  const format = view.getUint16(6, true);
  if (version !== 1 || format !== 1) {
    throw new Error(`unsupported peak format ${version}/${format}`);
  }

  const count = view.getUint32(8, true);
  const bucketsPerSecond = view.getFloat32(12, true);
  const expected = HEADER_BYTES + count * 2;
  if (buffer.byteLength < expected) {
    throw new Error("peak data is shorter than its header claims");
  }

  return {
    values: new Int8Array(buffer, HEADER_BYTES, count * 2),
    count,
    bucketsPerSecond,
  };
}

export async function fetchPeaks(
  trackId: number,
  res: "overview" | "detail",
  signal: AbortSignal,
): Promise<PeakData> {
  const response = await fetch(`/api/tracks/${trackId}/peaks?res=${res}`, { signal });
  if (!response.ok) {
    throw new Error(`peaks unavailable (${response.status})`);
  }
  return parsePeaks(await response.arrayBuffer());
}

export interface BeatData {
  bpm: number | null;
  source: "override" | "detected" | null;
  beats: Float64Array;
}

export async function fetchBeats(trackId: number, signal: AbortSignal): Promise<BeatData> {
  const response = await fetch(`/api/tracks/${trackId}/beats`, { signal });
  if (!response.ok) {
    throw new Error(`beats unavailable (${response.status})`);
  }
  const body = (await response.json()) as {
    bpm: number | null;
    source: "override" | "detected" | null;
    beats: number[];
  };
  return { bpm: body.bpm, source: body.source, beats: Float64Array.from(body.beats) };
}
